import type { RealtimeSubscriptionAdapter } from '../types/subscription-adapter.types';
import type { RealtimeSubscriptionOperation } from '../types/realtime-client.type';

/**
 * Normalizes and deduplicates requested topic names.
 * @param topics - Requested topics.
 * @returns Non-empty lowercase topic names.
 */
export const normalizeTopics = (topics: string[]): string[] =>
  Array.from(
    new Set(
      topics
        .map((topic) => topic.trim().toLowerCase())
        .filter((topic) => topic.length > 0)
    )
  );

/**
 * Splits normalized topics at a protocol-owned request limit.
 * @param topics - Topics owned by one subscription adapter.
 * @param maximum - Positive protocol request bound.
 * @returns Bounded topic batches.
 */
export const chunkTopics = (topics: string[], maximum: number): string[][] => {
  const chunks: string[][] = [];
  for (let index = 0; index < topics.length; index += maximum) {
    chunks.push(topics.slice(index, index + maximum));
  }
  return chunks;
};

/**
 * Selects one owner without falling through a disabled specialized policy.
 * @param adapters - Ordered application subscription policies.
 * @param topic - Normalized requested topic.
 * @returns The first matching adapter, if any.
 */
export const findSubscriptionAdapter = (
  adapters: readonly RealtimeSubscriptionAdapter[],
  topic: string
): RealtimeSubscriptionAdapter | undefined =>
  adapters.find((adapter) => adapter.matchesTopic(topic));

/**
 * Serializes each adapter's topics separately, preserving protocol boundaries.
 * @param adapters - Ordered application subscription policies.
 * @param operation - Desired subscription operation.
 * @param topics - Current normalized desired topics.
 * @param token - Current account access token.
 * @returns Subscription frames for enabled owners and teardown frames for all owners.
 */
export const serializeSocketRequests = (
  adapters: readonly RealtimeSubscriptionAdapter[],
  operation: RealtimeSubscriptionOperation['type'],
  topics: string[],
  token: string
): string[] =>
  [...adapters]
    // Preserve ordinary subscriptions before protected handshakes.
    .sort(
      (left, right) =>
        Number(left.requiresAuthorization) - Number(right.requiresAuthorization)
    )
    .flatMap((adapter) => {
      const ownedTopics = topics.filter(
        (topic) => findSubscriptionAdapter(adapters, topic) === adapter
      );
      return ownedTopics.length &&
        (operation === 'unsubscribe' || adapter.isEnabled())
        ? adapter.serializeRequests(operation, ownedTopics, token)
        : [];
    });
